import React, { createContext, useContext, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modal — the ONE centred dialog primitive (#479 follow-up). Every dialog in
 * the product renders through this, so viewport and scroll behaviour live in
 * one place instead of ~40 hand-rolled `fixed inset-0` scrims. A guard test
 * (modalGuard.test.js) fails CI when a new hand-rolled scrim appears.
 *
 * What it owns:
 *   • a portal to <body> — a transformed / backdrop-blurred ancestor (every
 *     .pai-panel) is a containing block for `position: fixed`, so an in-tree
 *     scrim would be clipped to the panel (#225);
 *   • the scrim: `fixed inset-0`, centred, 1rem gutters, the design-system
 *     wash (`bg-black/[0.58] backdrop-blur-scrim`);
 *   • the card, bounded to the DYNAMIC viewport via the shared index.css
 *     classes, scrolling inside itself with `overscroll-behavior: contain`.
 *     The app shell is already `h-dvh overflow-hidden`, so there is NO body
 *     scroll lock (those break iOS and leave the page frozen on a missed
 *     cleanup);
 *   • Escape — capture phase on document + stopPropagation (the panel/menu
 *     convention), and only the TOPMOST open Modal reacts when several stack;
 *   • backdrop close — mousedown AND click must both land on the scrim, so a
 *     text-selection drag that ends outside the card never closes it;
 *   • clicks never bubble out of it (React-tree portal bubbling);
 *   • role="dialog" aria-modal="true" (+ aria-labelledby).
 *
 * layout:
 *   "bounded" — the whole card scrolls (.pai-dialog-bounded). Short dialogs.
 *   "frame"   — header / scrolling body / pinned footer (.pai-dialog-frame +
 *               .pai-dialog-body): long forms keep their primary action on
 *               screen. `header`/`title` and `footer` render outside the body.
 *   "column"  — a bounded flex column whose children the caller lays out
 *               (it MUST give one child `min-h-0 overflow-y-auto` or
 *               .pai-dialog-body). For dialogs with their own sectioning.
 *
 * `surface` replaces the default card skin (opaque bg-pai-modal, border,
 * rounded-modal, shadow-modal) for the rare dialog with its own.
 *
 * zIndex is a literal Tailwind class (`z-[80]`) so JIT sees it — and a few
 * E2E specs select the scrim by `div.fixed.inset-0.z-[80]`.
 *
 * Layering: portaling lifts a dialog OUT of its host's stacking context, so a
 * z-50 dialog opened from a z-70 dialog would paint behind it. A Modal nested
 * (in the React tree) inside another Modal — or inside a <ModalLayer> host such
 * as the File Manager surface — is therefore always raised to at least one
 * above its host, whatever its own class says.
 */

// Width presets. "none" sets no width at all, for a card that sizes itself
// in cardClassName (two width utilities on one element would fight).
const SIZES = {
    sm: 'w-full max-w-sm',
    md: 'w-full max-w-md',
    lg: 'w-full max-w-lg',
    xl: 'w-full max-w-2xl',
    full: 'w-full max-w-none',
    none: '',
};

// Scrim alignment. "center" is the dialog; "fill" stretches the card to the
// full phone screen (the caller adds its `sm:` centring/padding in className);
// "bottom" docks it as a sheet. Only "center" carries the 1rem gutters.
const PLACEMENTS = {
    center: 'items-center p-4',
    fill: 'items-stretch',
    bottom: 'items-end',
};

const SURFACE = 'bg-pai-modal border border-pai rounded-modal shadow-modal';

// Open Modals, oldest first. Only the last one answers Escape.
const stack = [];

// The z-index of the nearest enclosing dialog layer (0 = none).
const LayerContext = createContext(0);
const zOf = (cls) => Number((/\bz-\[?(\d+)\]?/.exec(cls) || [])[1] || 0);

/** Declares a hand-rolled overlay host's z-index to the Modals inside it. */
export const ModalLayer = ({ z, children }) => (
    <LayerContext.Provider value={z}>{children}</LayerContext.Provider>
);

export default function Modal({
    open = true,
    onClose,
    children,
    size = 'md',
    layout = 'bounded',
    placement = 'center',
    title,
    header,
    footer,
    initialFocus,
    closeOnBackdrop = true,
    closeOnEscape = true,
    zIndex = 'z-50',
    className = '',
    cardClassName = '',
    bodyClassName = '',
    footerClassName = '',
    surface = SURFACE,
    scrimTestId,
    cardTestId,
    bodyTestId,
    footerTestId,
    labelledBy,
    ariaLabel,
    role = 'dialog',
    cardRef,
    cardProps,
    scrimProps,
    portal = true,
}) {
    const token = useRef({});
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const downOnScrim = useRef(false);
    const titleId = useId();
    const hostZ = useContext(LayerContext);
    const ownZ = zOf(zIndex);
    const z = Math.max(ownZ, hostZ + 1);

    // Stack membership — independent of closeOnEscape, so a topmost Modal that
    // handles Escape itself still shields the ones beneath it.
    useEffect(() => {
        if (!open) return undefined;
        const me = token.current;
        stack.push(me);
        return () => {
            const i = stack.lastIndexOf(me);
            if (i !== -1) stack.splice(i, 1);
        };
    }, [open]);

    useEffect(() => {
        if (!open || !closeOnEscape) return undefined;
        const me = token.current;
        const onKey = (e) => {
            if (e.key !== 'Escape' || stack[stack.length - 1] !== me) return;
            e.stopPropagation();
            onCloseRef.current?.();
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [open, closeOnEscape]);

    useEffect(() => {
        if (open && initialFocus?.current) initialFocus.current.focus({ preventScroll: true });
    }, [open, initialFocus]);

    if (!open) return null;

    const hasTitle = title != null && title !== false;
    const head = header ?? (hasTitle ? (
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-pai-hairline">
            <h3 id={titleId} className="text-[17px] font-bold text-white min-w-0 truncate">{title}</h3>
            <button type="button" onClick={onClose} aria-label="Close" className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0">
                <X size={18} />
            </button>
        </div>
    ) : null);

    const layoutClass = layout === 'bounded' ? 'pai-dialog-bounded' : 'pai-dialog-frame';
    const card = (
        <div
            {...cardProps}
            ref={cardRef}
            role={role}
            aria-modal="true"
            aria-labelledby={labelledBy ?? (hasTitle && !header ? titleId : undefined)}
            aria-label={ariaLabel}
            data-testid={cardTestId}
            className={`${layoutClass} ${SIZES[size] ?? ''} ${surface} ${cardClassName}`}
        >
            {layout === 'frame' ? (
                <>
                    {head && <div className="flex-shrink-0">{head}</div>}
                    <div data-testid={bodyTestId} className={`pai-dialog-body ${bodyClassName}`}>{children}</div>
                    {footer && <div data-testid={footerTestId} className={`flex-shrink-0 ${footerClassName}`}>{footer}</div>}
                </>
            ) : (
                <>
                    {head}
                    {children}
                    {footer && <div data-testid={footerTestId} className={footerClassName}>{footer}</div>}
                </>
            )}
        </div>
    );

    const scrim = (
        <div
            {...scrimProps}
            style={z !== ownZ ? { ...scrimProps?.style, zIndex: z } : scrimProps?.style}
            data-testid={scrimTestId}
            className={`fixed inset-0 ${zIndex} flex justify-center ${PLACEMENTS[placement] ?? PLACEMENTS.center} bg-black/[0.58] backdrop-blur-scrim ${className}`}
            onMouseDown={(e) => { downOnScrim.current = e.target === e.currentTarget; }}
            onClick={(e) => {
                // A portal still bubbles through the REACT tree: never let a
                // click inside the dialog reach the opener's own handlers
                // (a ticket card, a row) — the old cards' stopPropagation.
                e.stopPropagation();
                const both = downOnScrim.current && e.target === e.currentTarget;
                downOnScrim.current = false;
                if (both && closeOnBackdrop) onClose?.();
            }}
        >
            <LayerContext.Provider value={z}>{card}</LayerContext.Provider>
        </div>
    );

    return portal ? createPortal(scrim, document.body) : scrim;
}
